import { useAuth0 } from '@auth0/auth0-react';
import { CubeProvider } from '@cubejs-client/react';
import { DuckDBAccessMode } from '@duckdb/duckdb-wasm';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import * as Sentry from '@sentry/react';
import { security } from './app/security';
import useSyncApiAccessToken from './features/auth/hooks/useSyncApiAccessToken';
import LoadingPlant from './features/commonUI/LoadingPlant/LoadingPlant';
import theme from './features/commonUI/theme';
import initializeDuckDb from './features/duckDB/third-party-kit/init/initializeDuckDb';
import { AppRouter } from './features/router/AppRouter';
import { useInitCubeJs } from './services/data/customerSummary/hooks/useInitCubeJs';

const App = () => {
  const { isAuthenticated, isLoading, error, getAccessTokenSilently } =
    useAuth0();

  if (error) {
    console.log('Auth0 error: ', error);
  }
  // set getAccessTokenSilently in global module to reuse it outside a React component.
  // used to get login token first, then fetch data using token in header
  security.setAccessTokenSilently(getAccessTokenSilently);

  // Initialize Cube.js instance via hook
  const cubejsApi = useInitCubeJs(isAuthenticated && !isLoading);

  // Fetch and store the auth0 token
  useSyncApiAccessToken();

  initializeDuckDb({
    config: {
      accessMode: DuckDBAccessMode.READ_WRITE,
      query: {
        castBigIntToDouble: true,
        castDecimalToDouble: true,
        castTimestampToDate: true,
      },
    },
  });
  // LD is initialized within PageLayoutAndProviders

  if (isLoading) {
    return <LoadingPlant />;
  }

  return (
    <ThemeProvider theme={theme}>
      <CubeProvider cubejsApi={cubejsApi}>
        <CssBaseline />
        <AppRouter loading={!cubejsApi} />
      </CubeProvider>
    </ThemeProvider>
  );
};

export const ProfiledApp = Sentry.withProfiler(App);
